import { gql } from '@apollo/client';
import { Checkbox, useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { BrightnessIcon } from '~components/ui/icons/BrightnessIcon';
import { Slider } from '~components/ui/Slider';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayBrightnessV2Slider_ConfigFragment,
  useConfigUpdateBrightnessV2Mutation,
} from './__generated__/ConfigDisplayBrightnessV2Slider.graphql';

interface Props {
  config: ConfigDisplayBrightnessV2Slider_ConfigFragment;
  isDisabled?: boolean;
  onBrightnessLoadingStateChange: (state: boolean) => void;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayBrightnessV2Slider({
  config,
  isDisabled = false,
  onBrightnessLoadingStateChange,
  configStatus,
}: Props) {
  const toast = useToast();
  const [brightnessLevel, setBrightnessLevel] = useState(0);
  const [autoBrightness, setAutoBrightness] = useState(false);
  const analytics = useAnalyticsReporter();
  const { t } = useTranslation();

  useEffect(() => {
    const newBrightnessLevel = config.brightnessV2?.value;

    if (!isNil(newBrightnessLevel)) {
      setBrightnessLevel(newBrightnessLevel);
    }

    const newAutoBrightness = config.brightnessV2?.autoBrightness;

    if (!isNil(newAutoBrightness)) {
      setAutoBrightness(newAutoBrightness);
    }
  }, [config.brightnessV2?.value, config.brightnessV2?.autoBrightness]);

  const [updateBrightnessV2, { loading }] = useConfigUpdateBrightnessV2Mutation();
  useEffect(() => {
    onBrightnessLoadingStateChange(loading);
  }, [loading, onBrightnessLoadingStateChange]);

  const handleUpdate = useCallback(
    async (value: number, auto: boolean) => {
      try {
        await updateBrightnessV2({
          variables: {
            input: {
              configurationId: config.id,
              value,
              autoBrightness: auto,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
        analytics.track('displaySettingUpdate', {
          group: 'playback',
          changeItem: 'brightnessV2',
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('brightnessUpdateErr'),
          description: fromError(err, 'ConfigUpdateBrightnessV2', {
            BRIGHTNESS_OUT_OF_RANGE: t('brightnessUpdateErrDesc'),
          }),
        });
      }
    },
    [analytics, updateBrightnessV2, config.id, toast, t],
  );

  const handleSliderChangeEnd = (value: number) => {
    handleUpdate(value, autoBrightness);
  };

  const handleAutoBrightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoBrightness(checked);

    // trigger update immediately when toggled
    handleUpdate(brightnessLevel, checked);
  };

  const disabled = checkStateToDisableTab(configStatus) ? true : isDisabled;

  return (
    <>
      <Checkbox
        isChecked={autoBrightness}
        onChange={handleAutoBrightnessChange}
        mb={2}
        isDisabled={disabled}
      >
        {t('autoBrightness')}
      </Checkbox>

      <Slider
        value={brightnessLevel}
        onChange={setBrightnessLevel}
        onChangeEnd={handleSliderChangeEnd}
        leftIcon={BrightnessIcon}
        min={0}
        max={100}
        isDisabled={disabled || autoBrightness}
      />
    </>
  );
}

ConfigDisplayBrightnessV2Slider.graphql = {
  fragment: {
    ConfigDisplayBrightnessV2Slider_config: gql`
      fragment ConfigDisplayBrightnessV2Slider_config on ConfigurationModel {
        id
        brightnessV2 {
          value
          autoBrightness
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateBrightnessV2: gql`
      mutation ConfigUpdateBrightnessV2($input: ConfigurationUpdateBrightnessV2Input!) {
        configurationUpdateBrightnessV2(input: $input) {
          ...ConfigDisplayBrightnessV2Slider_config
        }
      }
    `,
  },
};
